// موجودیت‌های دامنه - مستقل از Supabase و UI (بخش ۴.۲ سند معماری)

export type UserId = string;
export type SignalId = string;

export type SignalKind = 'definite' | 'if_company' | 'hosting' | 'open_idea' | 'poll' | 'fyi';
export type TimeMode = 'exact' | 'approx' | 'coordinate' | 'from_availability' | 'multi_option';
export type SignalStatus =
  | 'draft' | 'scheduled' | 'active' | 'confirmed' | 'completed' | 'cancelled' | 'expired'
  // فیلترِ واژه‌ها گیر داد: ساخته شده ولی برای کسی نرفته (مایگریشن 0174).
  | 'held';
export type ResponseKind = 'pending' | 'in' | 'out' | 'maybe' | 'conditional' | 'expired';

export interface Profile {
  id: UserId;
  username: string;
  displayName: string;
  avatarEmoji: string;
  city: string;
  country?: string | null;
  /**
   * شهرِ دیفالت — همون `city` بالا، ولی به‌شکلِ شناسه‌ی جدولِ `cities`.
   *
   * دو تا ستون برای یه چیز، چون دو تا کارِ متفاوت می‌کنن: `city` رشته‌ایه که
   * روی کارتِ آدم‌ها نوشته می‌شه (بدونِ هیچ join‌ای)، و این یکی چیزیه که
   * فهرست‌ها باهاش تقسیم می‌شن. هر دو با هم نوشته می‌شن — نگاه کن به
   * profileRepo.updateMine و مایگریشن 0063.
   */
  cityId?: number | null;
  statusText?: string | null;
  /** مسیر عکس داخل باکتِ `avatars` (مایگریشن 0006). */
  avatarPath?: string | null;
  /** آدرس عمومیِ همون عکس؛ باکت عمومیه پس امضا لازم نداره. */
  avatarUrl?: string | null;
  /**
   * زبانی که این آدم صریحاً انتخاب کرده. `null` یعنی هیچ‌وقت انتخاب نکرده.
   *
   * ⚠ اپ **از این نمی‌خواند** — زبانِ نمایش از حافظه‌ی خودِ دستگاه می‌آید و
   * همان‌جا هم می‌ماند. تنها مصرفش سرور است: تریگرهای اعلان (مایگریشن ۰۱۰۹)
   * متنِ پوش را از رویش می‌سازند، چون وقتی اپ بسته است هیچ کدی از ما روی
   * گوشی نیست که ترجمه کند.
   *
   * این‌جا در تایپ هست تا `prefLogRepo.syncLocale` بتواند بدونِ یک کوئریِ
   * اضافه بفهمد سرور از قبل همین را دارد یا نه.
   */
  locale?: string | null;
  /**
   * شماره‌ی نسخه‌ی سندِ حقوقی که این آدم پذیرفته (مایگریشن 0026).
   *
   * `null` یعنی هیچ‌وقت چیزی نپذیرفته — حسابی از پیش از آن مایگریشن. قضاوت
   * درباره‌اش این‌جا نیست، در `needsTermsAcceptance` است (core/legal/terms).
   */
  termsVersion?: number | null;
  isAdmin?: boolean;
}

export interface Tag {
  id: number;
  slug: string;
  emoji: string;
  labelFa: string;
  labelEn: string;
  /**
   * برچسب به ازای هر زبان — کلیدش کدِ زبان است (WHOSUP-212، جدولِ
   * `tag_labels`، مایگریشن 0144).
   *
   * منبعِ برچسبِ نمایشی همین است، نه دو ستونِ بالا: زبانِ چهاردهم یک ردیفِ
   * تازه در دیتابیس است و این نوع هیچ تغییری نمی‌کند. `labelFa`/`labelEn`
   * تا مهاجرتِ کاملِ مصرف‌کننده‌ها می‌مانند و دیتابیس خودش از همین ردیف‌ها
   * پرشان می‌کند.
   *
   * `en` همیشه هست (تریگرِ 0144 تضمینش می‌کند). نبودِ بقیه یعنی «انگلیسی
   * نشان بده» — قاعده در `lib/tagLabel.ts`.
   *
   * اختیاری است چون تگِ حالتِ دمو از دیتابیس نمی‌آید؛ آن‌جا همان دو ستون
   * جواب می‌دهند.
   */
  labels?: Record<string, string>;
  /**
   * تگِ مادر (مایگریشن 0084) — سلسله‌مراتبی که ادمین دستی تعریف می‌کند.
   *
   * تا وقتی مادر روی ردیفِ دسته‌هاست، فرزندهایش نمایش داده نمی‌شوند؛ با
   * فیلتر کردن روی مادر باز می‌شوند. قاعده‌اش در lib/tagCounts است.
   */
  parentId?: number | null;
  /**
   * کنار گذاشته شده (مایگریشن 0086) — تگ هست ولی هیچ‌جای اپ دیده نمی‌شود.
   *
   * فقط سربرگِ ادمین این را می‌بیند: `tagRepo.list` تگِ بایگانی را
   * اصلاً برنمی‌گردانند، پس هیچ فرم و هیچ ردیفِ دسته‌ای لازم نیست خودش
   * چکش کند. `null` یعنی فعال.
   */
  archivedAt?: string | null;
}

/**
 * کشور و شهرِ فهرستِ مرجع (مایگریشن 0050).
 *
 * پروفایل خودش این‌ها رو نگه نمی‌داره؛ فقط `name`شون رو. این دو تا فقط برای
 * انتخاب کردن‌ان — از لحظه‌ای که کاربر انتخاب کرد، بقیه‌ی اپ با یه رشته طرفه.
 */
export interface Country { code: string; name: string; emoji?: string | null; }
export interface City { id: number; name: string; countryCode: string; region?: string | null; }

/**
 * شهری که اپ توش محتوا داره — یعنی کرالر براش گشته (جدولِ `app_cities`،
 * مایگریشن 0063).
 *
 * `City` می‌گه دنیا چه شهرهایی داره، این می‌گه ما کدوم‌هاش رو داریم. مختصات
 * همراهشه چون «نزدیک‌ترین شهرِ دارای محتوا» بدونش قابلِ حساب نیست — نگاه کن
 * به lib/cityScope.ts.
 */
export interface AppCity extends City {
  lat?: number | null;
  lng?: number | null;
  countryName?: string | null;
}

/**
 * فهرستِ کاملِ تگ‌های یه رخداد/بیزینس (مایگریشن 0044).
 * categoryId اولین تگه، برای جاهایی که هنوز تک‌تگی مصرفش می‌کنن (رنگِ
 * نقشه، پیش‌پرِ سیگنال). اگه tagIds نیومده (دیتای دمو یا کشِ قدیمی)،
 * categoryId تنها منبعیه که داریم.
 */
export const tagIdsOf = (x: { tagIds?: number[] | null; categoryId?: number | null }): number[] =>
  (x.tagIds && x.tagIds.length > 0) ? x.tagIds : (x.categoryId != null ? [x.categoryId] : []);

/**
 * ردیفِ سبکِ «فهرستِ کاملِ شهر» — فقط برای شمردنِ دسته‌ها.
 *
 * چرا لازم شد: صفحه‌ی کشف سی‌تا سی‌تا بار می‌شه، پس شمارشی که روی
 * ردیف‌های بارشده انجام بشه تا وقتی کاربر تا ته اسکرول نکرده **غلطه** —
 * «موسیقی ۱۷» می‌نشست بالای شهری که ۱۹۰ رخدادِ موسیقی داشت.
 *
 * این شکل عمداً لاغره: نه عنوان، نه توضیح، نه عکس، نه مکان. فقط چیزی که
 * شمارش و فیلترهای غیرمتنی لازم دارن. برای اتاوا حدودِ ۱۱۰ کیلوبایتِ خام
 * (فشرده خیلی کمتر) در یک درخواسته، در برابرِ حدودِ یک مگابایتی که آوردنِ
 * کلِ رخدادها می‌شد.
 *
 * جستجوی متنی این‌جا نیست و عمدیه: متنِ رخداد همون چیزیه که این شکل رو
 * سنگین می‌کنه. تا وقتی کاربر چیزی تایپ کرده، شمارش برمی‌گرده روی همون
 * ردیف‌های بارشده — که همون‌هان هم که فهرست نشون می‌ده.
 */
export interface DiscoverIndexItem {
  id: string;
  kind: 'event' | 'business';
  tagIds: number[];
  categoryId: number | null;
  startsAt: string | null;
  endsAt: string | null;
  weekdays: number[] | null;
  monthdays: number[] | null;
  monthWeeks: number[] | null;
  isFree: boolean;
  hasDiscount: boolean;
}

export interface Condition { type: 'later' | 'day' | 'with_person' | 'other'; label: string; }

/**
 * رخدادِ کاتالوگی که یک سیگنال از رویش ساخته شده (`signals.event_id`).
 *
 * چرا لازم است: وقتی کسی از صفحه‌ی کشف یک رخداد را برمی‌دارد و ازش دعوت
 * می‌سازد، مکان و آدرس و صفحه‌ی رسمی مالِ آن رخدادند و ستون‌های خودِ
 * سیگنال خالی می‌مانند. بدونِ این، صفحه‌ی سیگنال فقط یک عنوان بود و یک
 * «این آخرِ هفته» — دقیقاً همان چیزی که یک بار روی یک دعوتِ واقعی
 * («Light at Night Kayaking») به چشم آمد.
 *
 * از دیتابیس با `signal_event_json` می‌آید (مایگریشن 0081) و برای
 * کاربرِ حساب‌دار از خودِ جدول با یک join.
 */
export interface SignalEvent {
  id: string;
  /** اسمِ محل («Rideau Canal»). محلِ ثبت‌نشده ندارد. */
  venue?: string | null;
  address?: string | null;
  /** صفحه‌ی رسمیِ رخداد، بیرونِ اپ. */
  url?: string | null;
  imageUrl?: string | null;
  /**
   * بازه‌ی خودِ رخداد — که با «کِی؟»ِ سیگنال یکی نیست و جایش را هم
   * نمی‌گیرد: کایاکینگ ممکن است چهار ماه برقرار باشد، ولی میزبان «این
   * آخرِ هفته» را انتخاب کرده.
   */
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface Signal {
  id: SignalId;
  creatorId: UserId;
  creator?: Profile;
  kind: SignalKind;
  title: string;
  note?: string | null;
  categoryId?: number | null;
  /** همه‌ی تگ‌ها (signal_tags، مایگریشن 0093) - نگاه کن به tagIdsOf بالا. */
  tagIds?: number[] | null;
  timeMode: TimeMode;
  startsAt?: string | null;
  /**
   * پایانِ بازه‌ی چندروزه (مایگریشن 0057). خالی یعنی سیگنال یه روزه —
   * حالتی که تا قبل از این تنها حالتِ ممکن بود.
   */
  endsAt?: string | null;
  /**
   * ساعت انتخاب نشده؛ فقط روز(ها) معتبره.
   *
   * `startsAt` بازم یه لحظه‌ی کامله (ظهرِ همون روزِ محلی نگه داشته می‌شه تا
   * جابه‌جاییِ منطقه‌ی زمانی روز رو عوض نکنه)، ولی ساعتش معنی نداره و هیچ‌جا
   * نباید نشون داده بشه.
   */
  dateOnly?: boolean;
  approxLabel?: string | null;
  placeName?: string | null;
  costNote?: string | null;
  /** پر است فقط وقتی سیگنال از یک رخدادِ کاتالوگ زاده شده. */
  event?: SignalEvent | null;
  capacityMax?: number | null;
  rsvpDeadline?: string | null;
  /** حداقلِ نفرات — با رسیدن به این عدد خودکار قطعی می‌شود (مایگریشن 0131). */
  quorumMin?: number | null;
  chainLimit: number;
  /**
   * برنامه‌ی قطعی‌شده، اگر باشد — joinِ سبکِ `plans` که فقط `myPlans` پرش
   * می‌کند (WHOSUP-135). `undefined` یعنی این کوئری اصلاً نپرسیده.
   */
  plan?: { finalStartsAt: string; finalPlace?: string | null; outcome?: string } | null;
  status: SignalStatus;
  /** واژه‌هایی که سیگنال را نگه داشتند — فقط وقتی `status = 'held'` (0174). */
  heldWords?: string[] | null;
  /** اعتراضِ صاحبش به همان نگه‌داشتن: در انتظار، رد شد، یا قبول شد. */
  heldAppealStatus?: 'open' | 'upheld' | 'allowed' | null;
  /**
   * پر یعنی **مدیر** نگهش داشته، نه فیلتر (0175): دلیلش، و «در چه صورتی
   * دوباره دیده می‌شود». ویرایش آن را آزاد نمی‌کند؛ فقط رأیِ مدیر.
   */
  heldReason?: string | null;
  heldFix?: string | null;
  shareToken: string;
  createdAt: string;
  /**
   * شماره‌ی نسخه‌ی فعلی (مایگریشن 0072). با هر ویرایشِ محتوایی یکی بالا می‌ره.
   * سیگنالی که هیچ‌وقت ویرایش نشده روی ۱ می‌مونه.
   */
  version: number;
  // آمار سمت سازنده
  counts?: { in: number; out: number; maybe: number; pending: number; conditional: number };
  myResponse?: ResponseKind;
  myCondition?: Condition | null;
  /**
   * دلیلِ «نه». فعلاً فقط `not_my_thing` یعنی «اهل این جور فعالیت‌ها نیستم».
   * از خودِ `response` جداست چون هر دو حالت `out` هستن ولی معنیِ متفاوتی دارن:
   * یکی «این بار نه»، اون یکی «اصلاً این مدل برنامه مالِ من نیست».
   */
  myDeclineReason?: DeclineReason | null;
  /**
   * نسخه‌ای که **من** روش جواب دادم. کوچک‌تر بودنش از `version` یعنی سیگنال
   * بعد از جوابِ من عوض شده — همون چیزی که نوارِ هشدارِ صفحه‌ی سیگنال روش
   * سوار می‌شه. خالی یعنی هنوز جواب ندادم.
   */
  myRespondedVersion?: number | null;
}

export type DeclineReason = 'not_my_thing';

/**
 * راهی که این آدم به سیگنال رسیده (مایگریشن 0126).
 *
 * `sms_group` جداست چون گیرنده‌ش لینکِ عمومی گرفته که جواب نمی‌گیرد
 * (core/lib/smsInvite.ts) — آمارِ «منتظرِ پاسخ» نباید او را بشمارد.
 */
export type RecipientVia =
  | 'direct' | 'group' | 'chain' | 'email' | 'sms_personal' | 'sms_group';

export interface Recipient {
  id: number;
  signalId: SignalId;
  recipientId?: UserId | null;
  profile?: Profile;
  /**
   * دعوت‌شده‌ی بی‌حساب (مایگریشن 0066). دقیقاً وقتی پر است که `recipientId`
   * خالی باشد — خودِ جدول همین را به‌صورت `check` تضمین می‌کند.
   */
  invitedEmail?: string | null;
  /** شماره‌ی دعوت‌شده‌ی بی‌حساب (مایگریشن 0067). قرینه‌ی `invitedEmail`. */
  invitedPhone?: string | null;
  invitedName?: string | null;
  via: RecipientVia;
  /** گروهی که دعوت ازش آمده — فقط وقتی `via = 'group'` (مایگریشن 0126). */
  viaGroupId?: number | null;
  /** اولین باری که صفحه‌ی سیگنال را باز کرد (مایگریشن 0129). */
  seenAt?: string | null;
  /** چه کسی دعوتش کرد — برای زنجیره یعنی «چه کسی آوردش» (مایگریشن 0132). */
  invitedBy?: UserId | null;
  response: ResponseKind;
  condition?: Condition | null;
  declineReason?: DeclineReason | null;
  /** نسخه‌ای که این جواب روش داده شد (مایگریشن 0072). */
  respondedVersion?: number | null;
}

/**
 * یه نسخه از یه سیگنال (مایگریشن 0072).
 *
 * برخلافِ `EventVersion` که یه ردیفِ کاملِ `events` است، این فقط عکسِ
 * فیلدهای **قابلِ ویرایش** است: نه توکنِ اشتراک توش هست نه وضعیت، چون
 * اون‌ها با ویرایش عوض نمی‌شن و گذاشتنشون توی تاریخچه فقط این توهم رو
 * می‌ساخت که می‌شه به عقب برگردوندشون.
 */
export interface SignalVersion {
  signalId: SignalId;
  version: number;
  kind: SignalKind;
  title: string;
  note?: string | null;
  categoryId?: number | null;
  timeMode: TimeMode;
  startsAt?: string | null;
  endsAt?: string | null;
  dateOnly?: boolean;
  approxLabel?: string | null;
  placeName?: string | null;
  costNote?: string | null;
  chainLimit: number;
  createdAt: string;
}

export interface Plan {
  signalId: SignalId;
  finalStartsAt: string;
  finalPlace?: string | null;
  reminderMin: number;
  outcome: 'pending' | 'done' | 'cancelled' | 'partial';
  shareScope: 'none' | 'friends' | 'fof' | 'public';
}

export interface Message {
  id: number;
  signalId: SignalId;
  senderId?: UserId | null;
  senderName?: string;
  kind: 'text' | 'system' | 'status' | 'summary';
  body: string;
  createdAt: string;
  /** مدیر پنهانش کرده (0175) — فقط فرستنده‌اش آن را می‌بیند، با همین دلیل. */
  heldReason?: string | null;
  heldFix?: string | null;
  heldAppealStatus?: 'open' | 'upheld' | 'allowed' | null;
}

/**
 * عکسی که یکی از جمعِ برنامه بعدش فرستاده (مایگریشن 0141).
 *
 * `url` در جدول نیست — باکت private است و آدرس هر بار **امضا** می‌شود، با
 * عمرِ محدود. پس ذخیره‌کردنی نیست و `signalRepo.photos` هر بار تازه‌اش را
 * می‌سازد؛ چیزی که می‌ماند `path` است.
 */
export interface SignalPhoto {
  id: number;
  signalId: SignalId;
  uploaderId?: UserId | null;
  uploaderName?: string;
  path: string;
  url: string | null;
  createdAt: string;
}

export interface Friend {
  friendshipId: number;
  profile: Profile;
  status: 'pending_in' | 'pending_out' | 'accepted';
  plansTogether?: number;
}

/**
 * مخاطبِ ایمپورت‌شده از گوشی. برخلاف Friend یک‌طرفه‌ست:
 * لازم نیست طرف مقابل توی اپ باشه یا من رو به عنوان دوست داشته باشه.
 */
export interface ImportedContact {
  id: number;
  deviceId: string;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  emails: string[];
  phones: string[];
  relationships: { label: string; name: string | null }[];
  socialProfiles: { service: string; username: string | null; url: string | null; source: string }[];
  avatarPath?: string | null;
  /** signed URL کوتاه‌عمر که repo موقع خوندن می‌سازه. */
  avatarUrl?: string | null;
  company?: string | null;
  isFriend: boolean;
  hidden: boolean;
  source: 'device' | 'manual';
  matchedUserId?: UserId | null;
  matched?: Profile;
  syncedAt: string;
}

/** فرم «افزودن دوست» - چه دستی پر بشه چه از روی یه مخاطب پیش‌پر بشه. */
export interface ManualFriendDraft {
  name: string;
  /** چند شماره پشتیبانی میشه؛ ردیف‌های خالی موقع ذخیره حذف می‌شن. */
  phones: string[];
  emails: string[];
  /** لینک یا هندل شبکه‌ی اجتماعی، مثلاً `instagram.com/sara` یا `@sara` */
  socials: string[];
  /** URI محلیِ عکسِ گرفته‌شده از دفترچه‌ی گوشی، قبل از آپلود. */
  avatarLocalUri?: string | null;
  /** مسیرِ عکسِ قبلاً آپلودشده (موقع ویرایش). */
  avatarPath?: string | null;
}

/**
 * یکی از شماره‌های ثبت‌شده‌ی خودم.
 *
 * چند شماره مجازه (خونه، کار، شماره‌ی کشورِ قبلی) — چون هر کدومشون یه راهِ
 * پیدا شدنه. ولی دقیقاً یکی «پیش‌فرض» است: همونی که به عنوان شماره‌ی من
 * نشون داده میشه. نگاه کن به مایگریشن 0048.
 */
export interface MyPhone {
  value: string;
  isDefault: boolean;
}

/**
 * جوابِ `start_phone_verification` (مایگریشن 0060).
 *
 * این‌جا و نه کنارِ phoneRepo، چون demoStore هم می‌سازدشون و phoneRepo خودش
 * demoStore رو وارد می‌کنه — تعریفشون کنارِ ریپازیتوری یعنی حلقه‌ی import.
 */
export type StartVerifyResult =
  /** کد رفت؛ تا `expiresAt` معتبره. */
  | { status: 'sent'; expiresAt: string }
  /** از قبل ثبتِ خودته. */
  | { status: 'mine' }
  /** خیلی زود دوباره خواستی؛ `retryAfter` ثانیه صبر کن. */
  | { status: 'throttled'; retryAfter: number }
  /** سقفِ ساعتیِ کدها پر شده. */
  | { status: 'too_many' };

/** جوابِ `verify_phone` (مایگریشن 0060). */
export type VerifyPhoneResult =
  /** شماره آزاد بود و حالا مالِ توئه. */
  | { status: 'claimed' }
  /** مالِ یکی دیگه بود و همین حالا ازش گرفته شد. */
  | { status: 'taken' }
  | { status: 'mine' }
  /** کد غلط بود؛ `left` تلاشِ دیگه مونده. */
  | { status: 'wrong'; left: number }
  | { status: 'expired' }
  /** تلاش‌ها تموم شد — کدِ تازه لازمه. */
  | { status: 'locked' }
  /** برای این شماره کدی در جریان نیست. */
  | { status: 'none' };

export interface SyncResult { imported: number; matched: number; total: number; }
export interface SyncState {
  lastSyncedAt: string | null;
  deviceTotal: number;
  importedTotal: number;
}

/** به یک عضو گروه اشاره می‌کنه: یا کاربر اپ، یا یه مخاطب از دفترچه‌ی خودم. */
export type MemberRef =
  | { kind: 'user'; userId: UserId }
  | { kind: 'contact'; contactId: number };

export interface Group {
  id: number;
  name: string;
  emoji: string;
  /** همون فهرستِ دسته‌های رخداد و بیزینس (جدول tags). */
  categoryId?: number | null;
  ownerId: UserId;
  /** فقط شناسه‌ها؛ صفحه‌ی دوستان از لیستی که از قبل داره مچشون می‌کنه. */
  memberUserIds: UserId[];
  memberContactIds: number[];
}

/** فقط شناسه+هش - برای مقایسه‌ی سبک با کشِ رویِ گوشی، بدونِ کشیدنِ کل رکورد. */
export interface HashEntry { id: string; hash: string }

/**
 * فیلدهایی که کرالر برایشان «نسخه‌ی دیگر» می‌فرستد (مایگریشن ندارد — داخلِ
 * `events.metadata` می‌نشیند، چون اسکیمای `public` مالِ goout است).
 *
 * فقط چیزهایی که آدم واقعاً می‌تواند بینشان قضاوت کند. اختلافِ **عنوان و
 * توضیح و عکس** عمداً نمی‌آید: «دو سایت توضیح را دو جور نوشته‌اند» سؤالی
 * نیست که جوابِ آدم داشته باشد.
 */
export type AltFieldName =
  | 'starts_at' | 'ends_at' | 'venue_name' | 'venue_address'
  | 'price_note' | 'is_free' | 'lat' | 'lng' | 'weekdays';

/** یک ادعا درباره‌ی یک فیلد، از یک منبعِ مشخص. */
export interface FieldAlternative {
  /** خامِ همان چیزی که منبع گفته — ISO برای زمان، بولی برای `is_free`، … */
  value: unknown;
  /** نامِ منبع، همان‌طور که کرالر می‌شناسدش: `613cal`، `todocanada`، … */
  source: string;
  /** صفحه‌ای که این ادعا از آن درآمده — کاربر می‌تواند برود ببیند. */
  url?: string | null;
  /** همانی که در ستونِ خودِ ردیف نشسته. */
  selected: boolean;
}

export type FieldAlternatives = Partial<Record<AltFieldName, FieldAlternative[]>>;

export interface EventItem {
  id: string;
  /**
   * شناسه‌ی عددیِ کوتاهِ آیتم (مایگریشن 0051) - فقط به ادمین نشون داده می‌شه.
   *
   * برای کلِ رشته‌ی نسخه‌هاست نه هر ردیف، پس ویرایش عوضش نمی‌کنه. جایی که
   * دیده می‌شه به‌صورت «۱۲۳۴/۲» است: عدد، ممیز، شماره‌ی نسخه.
   */
  shortId?: number;
  title: string;
  description?: string | null;
  /**
   * همان `description`، ولی با مارک‌داونِ محدود — از `metadata.description_rich`
   * که کرالر می‌نویسد (WHOSUP-227). فقط وقتی هست که چیزی به ستون اضافه کند.
   *
   * ⚠ فقط **خواندنی**: اصلاحِ ادمین روی ستونِ `description` انجام می‌شود و هیچ
   * فرمی این را نمی‌نویسد. نگاه کن به lib/richText.ts.
   */
  descriptionRich?: string | null;
  categoryId?: number | null;
  /** همه‌ی تگ‌ها - نگاه کن به tagIdsOf بالا. */
  tagIds?: number[] | null;
  venueName?: string | null;
  lat?: number | null;
  lng?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  /** زمان شروعِ اجراهای اضافه؛ کارت کوچک از آن برای تشخیص چند ساعت متفاوت استفاده می‌کند. */
  occurrenceStartsAt?: string[];
  isFree: boolean;
  priceNote?: string | null;
  isPromoted: boolean;
  url?: string | null;
  /**
   * لینکِ خریدِ بلیط، جدا از `url` (مایگریشن 0077).
   *
   * `url` صفحه‌ی معرفیِ رخداده و این یکی صفحه‌ی پرداخت. یکی کردنشون یعنی کسی
   * که فقط می‌خواد بلیط بخره باید اول یه صفحه‌ی معرفی رو بگرده.
   */
  ticketUrl?: string | null;
  imageUrl?: string | null;
  /**
   * آدرسِ اینترنتیِ همون عکس، وقتی `imageUrl` نسخه‌ی کش‌شده‌ی رویِ دیسکه.
   * فقط از کشِ آفلاین می‌آد، نه از سرور — نگاه کن به lib/offlineCache.ts.
   */
  remoteImageUrl?: string | null;
  /**
   * نامِ شهرِ همون رخداد/بیزینس (نه شهرِ فیلترِ کوئری) - برای پاکسازیِ آدرسِ
   * کارت لازمه. از `cities` می‌آد (join)، نه از یه ستونِ متنی — مایگریشن 0063.
   */
  city?: string | null;
  /** شناسه‌ی همون شهر. چیزی که فهرست‌ها باهاش تقسیم می‌شن. */
  cityId?: number | null;
  /** فقط برای رخدادهای تکرارشونده: ۰=شنبه … ۶=جمعه (نگاه کن به days() توی i18n.ts). */
  weekdays?: number[] | null;
  /** رخدادِ ماهانه‌ی روزمحور: روزهای ماه، ۱..۳۱ (مایگریشن 0057). */
  monthdays?: number[] | null;
  /**
   * رخدادِ ماهانه‌ی هفته‌محور: هفته‌ی چندمِ ماه (۱..۵، و ‎-1 یعنی آخرین).
   * تنهایی معنی نداره؛ با `weekdays` می‌شه «اولین جمعه‌ی هر ماه».
   */
  monthWeeks?: number[] | null;
  /** هشِ محتوا (عنوان+تاریخ+توضیحات+عکس) - کشِ آفلاینِ گوشی با همین تشخیص می‌ده چی عوض شده (مایگریشن 0040). */
  contentHash?: string | null;
  /**
   * لحظه‌ی افزوده شدن به دیتابیس (مایگریشن 0033) - یکی از ترتیب‌های صفحه‌ی کشف.
   *
   * رکوردی که از قبل توی کشِ گوشی نشسته این رو نداره (هشِ محتواش عوض نشده
   * که دوباره گرفته شه)، برای همین ترتیبِ «زمانِ افزودن» عددش رو از
   * event_popularity می‌گیره - نگاه کن به src/core/data/popularityRepo.ts.
   */
  createdAt?: string | null;
  /**
   * events و businesses یه جدولن (مایگریشن 0042) - این ستون فرق می‌ذاره.
   * فیلدهایی که پایین‌تر می‌آد (hasDiscount/discountNote/address) فقط
   * برای kind='business' معنی دارن؛ startsAt/isFree/... فقط برای 'event'.
   */
  kind?: 'event' | 'business';
  /** فقط برای kind='business' - نگاه کن به مایگریشن 0042. */
  hasDiscount?: boolean;
  discountNote?: string | null;
  /** فقط برای kind='business'؛ رخداد آدرسش رو از venue می‌گیره نه این ستون. */
  address?: string | null;
  /** ریشه‌ی رشته‌ی نسخه‌ها - خودِ ریشه بودن یعنی rootId همون id است (مایگریشن 0033). */
  rootId?: string;
  version?: number;
  /**
   * کی این ردیف رو فرستاده (مایگریشن 0012). فقط خودش و ادمین می‌بیننش —
   * سیاستِ `e_sel` هم دقیقاً روی همین می‌چرخه.
   */
  submittedBy?: string | null;
  /**
   * فرستنده گفته خودش برگزارکننده‌ست، نه فقط خبررسان (مایگریشن 0077).
   * تنها اثرش اینه که ویرایشِ خودش بازبینی می‌خواد یا نه.
   */
  isOrganizer?: boolean;
  /**
   * `merged` یعنی این آیتم توی یکی دیگه ادغام شده (مایگریشن 0051) - برگشت‌پذیره.
   * `withdrawn` یعنی خودِ فرستنده پسش گرفته (مایگریشن 0078) — عمداً از
   * `rejected` جداست: اون یعنی «ما ردش کردیم».
   */
  status?: 'pending_review' | 'published' | 'rejected' | 'expired' | 'superseded'
    | 'merged' | 'withdrawn';
  editNote?: string | null;
  editedBy?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNote?: string | null;
  /**
   * ستون‌هایی از همین ردیف که ادمین نوشته و کرالر حق بازنویسی‌شان را ندارد
   * (مایگریشن 0096). نامشان **نامِ ستونِ دیتابیس** است، نه نامِ این تایپ:
   * قراردادش با کرالر است، نه با اپ.
   */
  lockedFields?: string[] | null;
  /**
   * «منبعِ دیگری چیزِ دیگری می‌گوید» — از `metadata.field_alternatives` که
   * کرالر می‌نویسد. نگاه کن به lib/fieldAlternatives.ts.
   */
  alternatives?: FieldAlternatives | null;
}

/**
 * یه جای همیشگی: کافه، رستوران، باشگاه… برخلاف رخداد، تاریخ نداره.
 * `url` اجباریه چون بدون منبع، پیشنهاد قابل راستی‌آزمایی نیست (مایگریشن 0012).
 */
export interface Business {
  id: string;
  /** همون شناسه‌ی عددیِ EventItem - جدولشون یکیه (مایگریشن 0042). */
  shortId?: number;
  name: string;
  description?: string | null;
  categoryId?: number | null;
  /** همه‌ی تگ‌ها - نگاه کن به tagIdsOf بالا. */
  tagIds?: number[] | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  url: string;
  imageUrl?: string | null;
  /** نگاه کن به `EventItem.remoteImageUrl`. */
  remoteImageUrl?: string | null;
  /** نامِ شهرِ همین بیزینس - برای پاکسازیِ آدرسِ کارت لازمه. */
  city?: string | null;
  /** شناسه‌ی همون شهر — نگاه کن به EventItem.cityId. */
  cityId?: number | null;
  /** هشِ محتوا - همون منطقِ EventItem.contentHash (مایگریشن 0040). */
  contentHash?: string | null;
  /** لحظه‌ی افزوده شدن - همون منطقِ EventItem.createdAt. */
  createdAt?: string | null;
  hasDiscount: boolean;
  discountNote?: string | null;
  isPromoted: boolean;
  status: 'pending_review' | 'published' | 'rejected' | 'merged';
}

/** چیزی که کاربر پیشنهاد می‌ده - رخداد یا بیزینس. */
export interface SuggestionDraft {
  kind: 'event' | 'business';
  name: string;
  url: string;
  description: string;
  /**
   * کجا. برای بیزینس همیشه بوده؛ از این به بعد رخداد هم داره.
   *
   * رخدادِ کاربر عمداً ردیفِ `venues` نمی‌سازه: چیزی که کاربر تایپ می‌کنه
   * راستی‌آزمایی نشده و جدولِ مشترکِ مکان‌ها رو شلوغ می‌کنه. پس همون
   * ستون‌های خودِ ردیف پر می‌شن — `itemFields` هم از قبل همین ترتیب رو
   * دنبال می‌کنه: اول نامِ venue، بعد address، بعد شهر.
   */
  address: string;
  /** نقطه‌ی روی نقشه، اگه کاربر انتخابش کرده باشه. */
  lat?: number;
  lng?: number;
  tagIds: number[];
  /**
   * فقط برای رخداد - و مثلِ فرمِ ویرایش، `Date` نه رشته.
   *
   * تا قبل از این هیچ‌جای فرم پرش نمی‌کرد: هر رخدادی که کاربر پیشنهاد می‌داد
   * بی‌تاریخ ثبت می‌شد و توی فهرستِ کشف ته صف می‌افتاد.
   */
  startsAt?: Date;
  /**
   * پایانِ رخداد. برای رخدادِ کاربر **اجباریه** (مایگریشن 0077): پینِ بالای
   * صفحه‌ی کشف دقیقاً تا همین لحظه می‌مونه، پس بدونش یا تا ابد می‌موند یا
   * اصلاً نمی‌شست.
   */
  endsAt?: Date;
  /**
   * الگوی تکرار و تاریخ‌های اضافه - همون چیزهایی که فرمِ ویرایش هم می‌ده.
   * هر دو فرم از یه انتخابگر استفاده می‌کنن (ui/EventDatePicker.tsx).
   */
  weekdays?: number[];
  monthdays?: number[];
  monthWeeks?: number[];
  occurrences?: OccurrenceDraft[];
  isFree: boolean;
  /** لینکِ خریدِ بلیط - فقط برای رخداد (مایگریشن 0077). */
  ticketUrl?: string;
  /** آدرسِ عکسی که همین الان آپلود شده - نگاه کن به eventRepo.uploadImage. */
  imageUrl?: string;
  /**
   * «خودم برگزارکننده‌م» در برابرِ «فقط دارم خبر می‌دم».
   *
   * ادعاست نه اثبات، و تنها اثرش روی **ویرایش‌های بعدیِ خودِ فرستنده**ست
   * (نگاه کن به `save_own_event` در مایگریشن 0077): برگزارکننده مستقیم
   * عوض می‌کنه، خبررسان از راهِ صفِ بازبینی.
   */
  isOrganizer?: boolean;
  /** فقط برای بیزینس. */
  hasDiscount: boolean;
  discountNote: string;
}

/**
 * یه تاریخِ پراکنده‌ی رخداد، همون‌طور که فرمِ ویرایش می‌شناسدش.
 *
 * از `EventOccurrence` جداست چون اون چیزیه که **خونده** می‌شه (با شناسه‌ی
 * سطر و نامِ مکانِ رزولوشده) و این چیزیه که **نوشته** می‌شه. عنوان و لینک
 * این‌جا هستن ولی فرم ویرایششون نمی‌کنه: فقط دست‌نخورده رد می‌شن تا ویرایشِ
 * یه تاریخ، لینکِ سانسِ کناری رو دور نریزه.
 */
export interface OccurrenceDraft {
  startsAt: Date;
  endsAt?: Date;
  title?: string | null;
  url?: string | null;
  venueId?: string | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
}

/** پیشنهادِ ویرایشِ یه رخدادِ موجود - تا تاییدِ ادمین دیده نمیشه (مایگریشن 0033). */
export interface EventEditDraft {
  /** می‌شه موقعِ ویرایش عوضش کرد - نگاه کن به مایگریشن 0042/0043. */
  kind: 'event' | 'business';
  title: string;
  description?: string;
  tagIds: number[];
  venueName?: string;
  venueAddress?: string;
  venueLat?: number;
  venueLng?: number;
  startsAt?: Date;
  endsAt?: Date;
  isFree: boolean;
  priceNote?: string;
  url: string;
  /** لینکِ خریدِ بلیط (مایگریشن 0077) - نگاه کن به `EventItem.ticketUrl`. */
  ticketUrl?: string;
  /** عکسِ کاور. خالی یعنی همون عکسِ فعلی نگه داشته نشه. */
  imageUrl?: string;
  weekdays?: number[];
  /** نگاه کن به `EventItem.monthdays`. */
  monthdays?: number[];
  /** نگاه کن به `EventItem.monthWeeks`. */
  monthWeeks?: number[];
  /**
   * تاریخ‌های پراکنده‌ی رخداد (`event_occurrences`، مایگریشن 0054).
   *
   * هر ویرایش یه ردیفِ تازه‌ی رخداد می‌سازه و اجراها به ردیفِ قبلی چسبیدن، پس
   * این فهرست باید **کامل** باشه: هرچی این‌جا نباشه، توی نسخه‌ی تازه نیست.
   */
  occurrences?: OccurrenceDraft[];
  /** فقط برای kind='business'. */
  hasDiscount?: boolean;
  discountNote?: string;
  address?: string;
  lat?: number;
  lng?: number;
}

/** یه نسخه از تاریخچه‌ی ویرایشِ یه رخداد - برای صفحه‌ی ادمین. */
export interface EventVersion extends EventItem {
  editedByName?: string | null;
  reviewedByName?: string | null;
}

/* ═══════════ جعبه‌ابزارِ ادمین (مایگریشن 0051) ═══════════ */

/**
 * یه آیتمِ ادغام‌شده، همون‌قدر که یه کارتِ فهرست لازم داره.
 *
 * `EventItem` این دو تا رو نداره چون مالِ چیزیه که کاربرِ عادی می‌بینه، و
 * چیزی که ادغام شده اصلاً بهش نشون داده نمی‌شه.
 */
export type MergedItem = EventItem & {
  mergedAt?: string | null;
  /** شناسه‌ی عددیِ برنده — «ادغام شد در #۱۲۳۴». */
  mergedIntoShortId?: number | null;
};

/**
 * چهار نشانه‌ی ادمین روی کارت‌های صفحه‌ی کشف، همگی برای **یک شهر**.
 *
 * چرا این‌ها ستونی روی خودِ رخداد نیستند: فهرستِ دیسکاور از کشِ رویِ گوشی
 * می‌آید و کش فقط وقتی تازه می‌شود که `content_hash` عوض شده باشد. یعنی
 * رکوردی که از قبل روی گوشی نشسته هیچ‌وقت خودش `short_id` یا `flagged_at`
 * به دست نمی‌آورد، و پاک کردنِ کلِ کش برای این یعنی همه‌ی کاربرها همه‌ی
 * عکس‌ها را دوباره دانلود کنند — برای چیزی که فقط ادمین می‌بیند.
 *
 * تا WHOSUP-209 چهار درخواستِ جدا و بی‌فیلترِ شهر بودند؛ حالا یک
 * رفت‌وبرگشت‌اند. نگاه کن به `adminRepo.discoverMarks` و مایگریشن 0143.
 */
export interface DiscoverMarks {
  /** uuid → شماره‌ی عددیِ آیتم. */
  shortIds: Record<string, number>;
  /** uuid → اولویتِ بازبینی (مایگریشن 0052). فقط آن‌هایی که عدد دارند. */
  priorities: Record<string, number>;
  /** شناسه‌ی آیتم‌هایی که خودشان از یک ترکیب در آمده‌اند — برنده‌ها، نه بازنده‌ها. */
  combined: string[];
  /** uuid → زمانِ نشان‌خوردن (ISO). سوختِ ترتیبِ «نشان‌دارها اول». */
  flagged: Record<string, string>;
}

/**
 * همون ردیف، ولی با ستون‌هایی که فقط ادمین لازمشون داره.
 *
 * `raw` عمداً کلِ ردیفِ برگشتی از دیتابیسه، نه یه انتخاب: «داده‌ی خامی که
 * گرفتیم» یعنی هرچی هست، حتی ستونی که فردا اضافه می‌شه و این‌جا اسمی نداره.
 */
export interface AdminItemDetail extends EventItem {
  shortId: number;
  source?: string | null;
  externalId?: string | null;
  metadata: Record<string, unknown>;
  flaggedAt?: string | null;
  flagNote?: string | null;
  flaggedByName?: string | null;
  mergedInto?: string | null;
  mergedAt?: string | null;
  mergedByName?: string | null;
  /** شناسه‌ی عددیِ آیتمی که این توش ادغام شده - برای لینک دادن بهش. */
  mergedIntoShortId?: number | null;
  raw: Record<string, unknown>;
}

/**
 * یه منبعی که این آیتم رو لیست کرده.
 *
 * `primary` همون ستونِ events.source است (منبعی که آورده‌ش)؛ بقیه از
 * event_verifications می‌آن (مایگریشن 0029) - از جمله فیدهای مستقلی که
 * همین آیتم رو گزارش کردن و منبعِ آیتم‌هایی که توش ادغام شدن.
 */
export interface ItemSource {
  name: string;
  kind: 'primary' | 'source_corroboration' | 'admin' | 'ai' | 'attendee_confirmed';
  note?: string | null;
  at?: string | null;
  externalId?: string | null;
}

/** یه تکراریِ محتمل - پیشنهادِ ادغام، نه تصمیمش. */
export interface DuplicateCandidate {
  id: string;
  shortId: number;
  title: string;
  kind: 'event' | 'business';
  status: string;
  startsAt?: string | null;
  url?: string | null;
  /**
   * چرا مشکوکه، به ترتیبِ اطمینان: هشِ محتوا، آدرس، عنوانِ یکسان، همون
   * لحظه و همون سالن، عنوانی که سرِ اون یکیه (مایگریشن 0055)، یا عنوانِ
   * نزدیک در همون ساعت (مایگریشن 0056).
   */
  reason: 'hash' | 'url' | 'title' | 'when' | 'prefix' | 'similar';
}

/**
 * یه اجرای رخداد: زمان و مکان و لینکِ خودش (مایگریشن 0054).
 *
 * ۹۰٪ رخدادها هیچ‌کدوم ندارن و همون یه زمان/مکانِ روی خودِ رکورد کافیه. این
 * سطرها وقتی ساخته می‌شن که ادمین موقعِ ترکیب بگه «هر دو رو نگه دار» — مثلاً
 * یه نمایشگاه که توی چند شهر و چند تاریخ برگزار می‌شه و هر کدوم صفحه‌ی
 * فروشِ خودش رو داره.
 */
export interface EventOccurrence {
  id: number;
  startsAt?: string | null;
  endsAt?: string | null;
  /** ارجاع به `venues` - فقط برای اینکه ویرایش مکانِ اجرا رو گم نکنه. */
  venueId?: string | null;
  venueName?: string | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  /** عنوانِ خاصِ همین اجرا (اسمِ دو تیم)، اگه با عنوانِ رخداد فرق داره. */
  title?: string | null;
  /** لینکِ خاصِ همین اجرا. خالی یعنی همون لینکِ خودِ رخداد. */
  url?: string | null;
}

/**
 * جفتی که ادمین گفته تکراری نیست (مایگریشن 0053) - از دیدِ همین آیتم، یعنی
 * «اون یکی». تا وقتی این ردیف هست، جفت دیگه پیشنهاد نمی‌شه.
 */
export interface DismissedDuplicate {
  id: string;
  shortId: number;
  title: string;
  note?: string | null;
  createdAt: string;
  byName?: string | null;
}

/** یه سطر از دفترِ کارهای ادمین. */
export interface AdminAction {
  id: number;
  action: 'delete' | 'restore' | 'flag' | 'unflag' | 'merge' | 'unmerge'
    | 'dismiss_dup' | 'restore_dup' | 'combine'
    /** تنها کنشی که بازیگرش ادمین نیست: صاحبِ رخداد پسش گرفته (مایگریشن 0078). */
    | 'withdraw'
    /**
     * ویرایشِ درجای ادمین روی یک ردیفِ کرالری (مایگریشن 0096). جانشینِ
     * زنجیره‌ی نسخه‌هاست، پس `detail` مقدارِ **قبلی و بعدیِ** هر ستونِ
     * عوض‌شده را دارد — تنها جایی که آن تاریخچه نوشته می‌شود.
     */
    | 'edit'
    /** «بگذار کرالر دوباره مدیریتش کند» — برداشتنِ قفلِ یک یا همه‌ی فیلدها. */
    | 'unlock';
  actorName?: string | null;
  detail: Record<string, unknown>;
  createdAt: string;
}

/** یه ادغامِ انجام‌شده - تا وقتی undoneAt نداره، برگشت‌پذیره. */
export interface ItemMerge {
  id: number;
  loserId: string;
  loserShortId?: number | null;
  loserTitle?: string | null;
  winnerId: string;
  winnerShortId?: number | null;
  winnerTitle?: string | null;
  note?: string | null;
  mergedAt: string;
  mergedByName?: string | null;
  undoneAt?: string | null;
}

export type Dow = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export interface AvailabilityCell { dow: Dow; hour: number; state: 'free' | 'locked'; }
export interface RankedSlot { dow: number; hour: number; freeCount: number; }

/**
 * تنظیمات اعلان (مایگریشن 0009).
 *
 * برای هر نوع، یکی از سه حالت:
 *   push  = اعلان واقعی روی گوشی
 *   inapp = فقط نشانگر داخل اپ
 *   off   = هیچی
 *
 * و یه تنظیم کلی که نشانگرِ داخل اپ چه شکلی باشه — نقطه یا عدد. عمداً کلیه
 * و برای هر نوع جدا نیست (درخواست پرویز): چیزی نیست که آدم بخواد برای
 * درخواست دوستی نقطه و برای سیگنال عدد ببینه.
 */
export type NotifCategory = 'friend_request' | 'signal' | 'event';
export type NotifMode = 'push' | 'inapp' | 'off';
/**
 * تگ‌های دنبال‌شده (مایگریشن 0094) تنها دسته‌ایه که «ایمیل» هم داره؛
 * برای همین تایپِ خودش رو داره و توی NotifMode قاطی نشده.
 */
export type TagFollowMode = NotifMode | 'email';
export type NotifIndicator = 'dot' | 'count';
export type NotifPrefs = Record<NotifCategory, NotifMode> & {
  /** کانالِ خبرِ «رخدادِ تازه با تگی که دنبال می‌کنی». پیش‌فرض push. */
  tag_follow: TagFollowMode;
  indicator: NotifIndicator;
  /**
   * بروزرسانی خودکارِ جاواسکریپت. پیش‌فرض روشن.
   * خاموش که باشه، بروزرسانی گرفته میشه ولی تا وقتی کاربر نگه اعمال نمیشه.
   * روی تغییرات نیتیو اثری نداره — اون‌ها همیشه نصب دستی می‌خوان.
   */
  auto_update: boolean;
};

export interface AppNotification {
  id: number;
  kind: string;
  /**
   * متنی که **سرور** ساخته، به زبانِ گیرنده در لحظه‌ی ساختِ سطر. همین متن
   * است که به FCM می‌رود، چون وقتی اپ بسته است هیچ کدی از ما روی گوشی
   * نیست که ترجمه کند.
   */
  title: string;
  body?: string | null;
  /**
   * همان متن، ولی به‌شکلِ کلیدِ دیکشنری + جاگذارها (مایگریشن ۰۱۱۲). هرجا
   * که **خودِ اپ** اعلان را نشان می‌دهد باید از این‌ها ساخته شود، نه از
   * `title`/`body` — وگرنه خبر به زبانِ روزِ ساخته‌شدنش می‌ماند و کسی که
   * زبانش را عوض کرده، اپِ انگلیسی و اعلانِ فارسی می‌بیند.
   *
   * `null` یعنی متن ترجمه‌پذیر نیست (عنوانِ «نامِ سازنده: عنوانِ برنامه») یا
   * سطر از قبلِ ۰۱۱۲ مانده. رندرش در `lib/notifText.ts` است.
   */
  titleKey?: string | null;
  titleVars?: Record<string, string> | null;
  bodyKey?: string | null;
  bodyVars?: Record<string, string> | null;
  /**
   * `alarm` از مایگریشن 0072: خبری که برای کسی رفته که گفته «هستم» —
   * از تنظیمِ پوشِ کاربر رد می‌شه و با نگاه کردن پاک نمی‌شه.
   */
  data: {
    signal_id?: string;
    friendship_id?: number;
    requester_id?: string;
    event_id?: string;
    tag_id?: number;
    notification_id?: number;
    alarm?: boolean;
    version?: number;
  };
  readAt?: string | null;
  /** زمان dispatch به صف push؛ وجودش یعنی FCM مسئول نمایش بوده است. */
  sentAt?: string | null;
  createdAt: string;
}

export interface SignalDraft {
  kind: SignalKind;
  title: string;
  note?: string;
  /**
   * همه‌ی تگ‌ها (جدول signal_tags، مایگریشن 0093). اولین‌شون همچنان توی
   * ستونِ قدیمیِ `category_id` هم نوشته می‌شه — نگاه کن به tagIdsOf بالا.
   */
  tagIds: number[];
  timeMode: TimeMode;
  startsAt?: Date;
  /** پایانِ بازه‌ی چندروزه (مایگریشن 0057). خالی یعنی یه روزه. */
  endsAt?: Date;
  /** ساعت انتخاب نشده - فقط روز(ها). نگاه کن به `Signal.dateOnly`. */
  dateOnly?: boolean;
  approxLabel?: string;
  placeName?: string;
  costNote?: string;
  /** ظرفیت — «هستم»ِ نفرِ مازاد در دیتابیس رد می‌شود (مایگریشن 0131). */
  capacityMax?: number | null;
  /** مهلتِ پاسخ — بعدش نه جوابِ تازه، نه تغییرِ جواب (مایگریشن 0131). */
  rsvpDeadline?: Date | null;
  /** حداقلِ نفرات — رسیدن به عدد یعنی قطعیِ خودکار؛ زمان اجباری می‌شود. */
  quorumMin?: number | null;
  chainLimit: number;
  /**
   * `picked` = فقط همون‌هایی که انتخاب شدن.
   * `friends` = «همه‌ی دوستانم»: `create_signal` آن را به ردیفِ گیرنده برای
   * هر دوستِ **همین لحظه** باز می‌کند و سیگنال `picked` ذخیره می‌شود — دوستی
   * که فردا اضافه شود نمی‌بیندش و جدا دعوت می‌شود (مایگریشن 0150، WHOSUP-260).
   * سیگنال‌هایی که پیش از 0150 با `friends` ساخته شدند پویا مانده‌اند (0018).
   */
  audience?: 'picked' | 'friends';
  recipientIds: UserId[];
  /**
   * گروه‌هایی که با انتخابشان آدم‌ها به `recipientIds` آمدند. برای ثبتِ
   * `via = 'group'` و `via_group_id` (مایگریشن 0126) — عضوی که در هیچ‌کدام
   * نباشد `direct` می‌شود. عضوِ دو گروهِ انتخاب‌شده به اولی نسبت داده می‌شود.
   */
  groupPicks?: { groupId: number; memberUserIds: UserId[] }[];
  /**
   * رخداد/مکانی که این سیگنال از روش ساخته شده (`signals.event_id`).
   *
   * تا حالا فقط عنوان و دسته‌ی رخداد کپی می‌شد و بعدش هیچ نخی بینشون
   * نمی‌موند. با این، «چند نفر از این رخداد سیگنال ساختن» قابلِ شمارشه —
   * یکی از اجزای امتیازِ محبوبیت (مایگریشن 0049).
   */
  eventId?: string;
}

/**
 * ویرایشِ یه سیگنالِ موجود (مایگریشن 0072).
 *
 * عمداً `SignalDraft` نیست و ازش ارث نمی‌بره: سه فیلدِ اون‌جا این‌جا معنی
 * ندارن — `recipientIds` (اضافه/کم کردنِ آدم کارِ جداییه، چون «کسی که گفته
 * هستم» رو نمی‌شه برداشت)، `audience` (عوض کردنش یعنی عوض کردنِ اینکه اصلاً
 * چه کسانی سیگنال رو می‌بینن) و `eventId` (رخدادی که سیگنال ازش زاده شده،
 * یه واقعیتِ گذشته‌ست).
 */
export interface SignalEditDraft {
  kind: SignalKind;
  title: string;
  note?: string | null;
  /** همون قصه‌ی SignalDraft.tagIds: جدول signal_tags + اولین تگ در category_id. */
  tagIds: number[];
  timeMode: TimeMode;
  startsAt?: Date | null;
  endsAt?: Date | null;
  dateOnly?: boolean;
  approxLabel?: string | null;
  placeName?: string | null;
  costNote?: string | null;
  capacityMax?: number | null;
  rsvpDeadline?: Date | null;
  quorumMin?: number | null;
  chainLimit: number;
}
